import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined } from '../effect-guns.sprite';

/**
 * Sky Ace (the Barnstormer set's gun, ADR-0160 amended): the nose of a biplane. A varnished wooden fuselage, two stubby
 * red wings over and under it on struts, a silver engine cowling ringed with its cylinders' heads, an exhaust stub
 * puffing, and at its front a propeller turning round a silver spinner (its blades long, short, end on). On a shot
 * (`fire`) the spinner flashes, the blades blur into a disc, then it settles.
 */

const WOOD = ['N', 'n', 'm', 't'] as const;

const plane = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The fuselage: varnished wood, its planks' seams.
    box(c, 4, 14, 44, 25, [3, 1], byRow(14, 25, WOOD, 2, 2), 'l');
    for (const x of [14, 26, 38]) c.vline(x, 16, 23, 'n');
    // The wings: red planks, the top one on struts, the lower one under the fist's reach.
    box(c, 18, 7, 40, 10, [1], (x, y): PaletteKey => (y === 7 ? 'i' : y === 10 ? 'R' : x % 6 === 0 ? 'R' : 'r'));
    for (const x of [22, 36]) c.vline(x, 11, 13, '3');
    box(c, 28, 26, 42, 28, [1], (_x, y): PaletteKey => (y === 26 ? 'r' : 'R'), 'r');
    // A roundel on the top wing.
    dots(c, 'w', [
      [29, 8],
      [30, 8],
      [29, 9],
      [30, 9],
    ]);
    // The cowling: a silver ring, its cylinders' heads round it.
    box(c, 43, 11, 52, 29, [3, 1], (x, y): PaletteKey => {
      if ((y - 11) % 4 === 1 && x > 44) return '2';
      return y < 14 ? '5' : y > 26 ? '3' : x === 43 ? '5' : '4';
    });
    // The exhaust stub on top.
    box(c, 44, 9, 46, 10, [], () => '2');
    // The trigger.
    box(c, 28, 26, 31, 29, [0, 1], (_x, y): PaletteKey => (y === 26 ? '3' : '2'), 'r');
    // The propeller: its blade across, at its length this frame, or a disc of blur as it fires.
    const reach = heat ? 11 : (([11, 6, 1, 6] as const)[frame % 4] as number);
    if (heat) for (let y = 9; y <= 31; y++) c.set(54, y, Math.abs(y - 20) > 9 ? 'Y' : y % 2 ? '5' : '4');
    for (let y = 20 - reach; y <= 20 + reach; y++) {
      const tip = Math.abs(y - 20) > reach - 2;
      c.set(53, y, tip ? 'y' : 'r');
      c.set(54, y, tip ? 'Y' : y < 20 ? 'i' : 'R');
      if (reach > 2) c.set(55, y, tip ? 'U' : 'R');
    }
    // The spinner: a silver cone to the muzzle.
    for (let x = 55; x <= 63; x++) {
      const h = Math.max(1, Math.round(4 - (x - 55) * 0.42));
      for (let y = 20 - h; y <= 20 + h - 1; y++) {
        const k: PaletteKey = heat >= 2 ? 'w' : heat === 1 ? 'y' : y === 20 - h ? 'w' : y >= 20 + h - 1 ? '3' : '5';
        c.set(x, y, k);
      }
    }
  });

export default sprite({
  name: 'pollinator_barnstormer',
  ...geometry,
  notes: `Sky Ace (ADR-0160 amended, the Barnstormer set's gun): the nose of a biplane. A varnished wooden fuselage (its
planks' seams), two stubby red wings over and under it (the top one on grey struts, a white roundel on it), a silver
engine cowling ringed by its cylinders' heads, an exhaust stub, and a red propeller with gold tips turning round a silver
spinner that narrows to the muzzle (long, short, end on, short over 4 frames). Fire: a disc of blur where the blades
swept, the spinner flashing white then gold, settling over 3 frames. Its flair: the propeller's blur glowing, exhaust
puffing off the stub. No team keys. Rubric (4×): S5 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: { fps: 12, frames: [plane(0), plane(1), plane(2), plane(3)] },
    fire: { fps: 20, loop: false, frames: [plane(0, 2), plane(1, 2), plane(2, 1), plane(3)] },
  },
});
